<!--
  신규 회원가입 + 강사 권한 요청 (guide01.md §2)
  ────────────────────────────────────────
  · 이메일 가입: 3-step (check-email → verify-email → access-request/signup 통합 제출)
    - 성공 시 계정(learner) 생성 + 강사 권한 요청 동시 접수
  · OAuth 가입: oauth/register(learner) 즉시 로그인 → 강사 정보 입력 → POST /instructor/access-request (signupPath:'new')
  · 공개 엔드포인트 3종은 auth 스토어와 동일하게 raw axios 사용 (Bearer 헤더 불필요)
-->
<template>
  <div class="p-login">
    <!-- 좌측 브랜드 -->
    <aside class="p-login__brand">
      <div class="p-login__brand-inner">
        <div class="p-login__logo">
          <img class="p-login__logo-img" src="/favicon.png" alt="Pineapple" />
          <div>
            <div class="p-login__brand-title">pineapple</div>
            <div class="p-login__brand-sub">INSTRUCTOR</div>
          </div>
        </div>
        <div class="p-login__hero">
          <svg viewBox="0 0 200 240" width="240" height="288" fill="none" aria-hidden="true">
            <g>
              <path d="M100 25 Q88 50 92 85 Q100 70 108 85 Q112 50 100 25 Z" fill="#7BB073" />
              <path d="M75 45 Q70 75 88 92 Q85 70 92 55 Q82 45 75 45 Z" fill="#4F8A5C" />
              <path d="M125 45 Q130 75 112 92 Q115 70 108 55 Q118 45 125 45 Z" fill="#4F8A5C" />
              <path d="M62 70 Q66 88 86 95 Q78 82 80 72 Q70 65 62 70 Z" fill="#7BB073" />
              <path d="M138 70 Q134 88 114 95 Q122 82 120 72 Q130 65 138 70 Z" fill="#7BB073" />
            </g>
            <path d="M100 90 C50 90 42 130 42 165 C42 205 65 220 100 220 C135 220 158 205 158 165 C158 130 150 90 100 90 Z" fill="#FFC93C" stroke="#C98A0A" stroke-width="2.5" />
            <g opacity="0.35" stroke="#E5A91A" stroke-width="1.5" fill="none">
              <path d="M60 130 L80 120 L100 130 L120 120 L140 130" />
              <path d="M60 130 L80 140 L100 130 L120 140 L140 130" />
              <path d="M60 160 L80 150 L100 160 L120 150 L140 160" />
              <path d="M60 160 L80 170 L100 160 L120 170 L140 160" />
              <path d="M60 190 L80 180 L100 190 L120 180 L140 190" />
            </g>
            <g fill="#E5A91A" opacity="0.55">
              <circle cx="80" cy="130" r="1.5" />
              <circle cx="120" cy="130" r="1.5" />
              <circle cx="100" cy="160" r="1.5" />
              <circle cx="80" cy="190" r="1.5" />
              <circle cx="120" cy="190" r="1.5" />
            </g>
            <g fill="#F5B5A8" opacity="0.7">
              <ellipse cx="86" cy="159" rx="6" ry="3.5" />
              <ellipse cx="114" cy="159" rx="6" ry="3.5" />
            </g>
            <g fill="none" stroke="#1F1A14" stroke-width="3.2" stroke-linecap="round">
              <path d="M83 151 Q88 145 93 151" />
              <path d="M107 151 Q112 145 117 151" />
            </g>
            <path d="M94 161 Q100 167 106 161" fill="none" stroke="#1F1A14" stroke-width="2.6" stroke-linecap="round" />
          </svg>
          <div class="p-login__hero-bubble">강사님, 반갑습니다!<br/>가입과 동시에 강사 권한을 요청할 수 있어요 ✨</div>
        </div>
      </div>
    </aside>

    <!-- 우측 폼 -->
    <main class="p-login__form-side">
      <div class="p-login__form-wrap">
        <div class="p-login__header">
          <div class="p-login__pill">FOR {{$CONF.APP_USERTYPE}}</div>
          <h1 class="p-login__title">강사 회원가입</h1>
          <p class="p-login__desc">계정 생성과 강사 권한 요청이 함께 접수됩니다.</p>
        </div>

        <!-- 스텝 표시 -->
        <div v-if="step !== 'done'" class="p-signup__steps">
          <span :class="stepClass('email')">1. 이메일</span>
          <span class="p-signup__steps-sep">›</span>
          <span :class="stepClass('code')">2. 인증 코드</span>
          <span class="p-signup__steps-sep">›</span>
          <span :class="stepClass('info')">3. 정보 입력</span>
        </div>

        <!-- ① 이메일 입력 -->
        <template v-if="step === 'email'">
          <div class="p-login__form">
            <div>
              <label class="p-login__label">이메일</label>
              <input
                v-model.trim="email"
                type="email"
                class="p-input"
                placeholder="teacher@pineapple.kr"
                :class="{ 'is-invalid': !!emailError }"
                @keyup.enter="sendCode"
              />
              <span v-if="emailError" class="p-login__err">{{ emailError }}</span>
              <div v-if="emailDuplicated" class="p-signup__hint-link">
                이미 계정이 있다면
                <router-link :to="{ name: 'Signin Cover' }">로그인</router-link>
                후 '강사 권한 요청'을 이용해주세요.
              </div>
            </div>
            <a-btn kind="primary" class="p-login__submit" :disabled="sending" @click="sendCode">
              {{ sending ? '발송 중…' : '이메일 인증 코드 받기' }}
            </a-btn>
          </div>

          <div class="p-login__divider"><span>또는</span></div>

          <o-auth-buttons :is-login="false" @success="onOAuthRegistered" @error="onOAuthError" />
        </template>

        <!-- ② 인증 코드 확인 -->
        <template v-else-if="step === 'code'">
          <div class="p-login__form">
            <p class="p-signup__notice">
              <b class="p-mono">{{ email }}</b> 으로 인증 코드를 발송했습니다.<br />
              메일에 안내된 코드를 입력해주세요.
            </p>
            <div>
              <label class="p-login__label">인증 코드</label>
              <input
                v-model.trim="code"
                type="text"
                inputmode="numeric"
                maxlength="10"
                class="p-input p-signup__code-input"
                placeholder="123456"
                :class="{ 'is-invalid': !!codeError }"
                @keyup.enter="verifyCode"
              />
              <span v-if="codeError" class="p-login__err">{{ codeError }}</span>
            </div>
            <a-btn kind="primary" class="p-login__submit" :disabled="verifying" @click="verifyCode">
              {{ verifying ? '확인 중…' : '인증 코드 확인' }}
            </a-btn>
            <div class="p-signup__code-actions">
              <a href="#" @click.prevent="resendCode">코드 재발송</a>
              <span class="p-login__links-sep">·</span>
              <a href="#" @click.prevent="backToEmail">이메일 변경</a>
            </div>
          </div>
        </template>

        <!-- ③ 계정 정보 + 강사 인증 정보 -->
        <template v-else-if="step === 'info'">
          <vee-form
            :validation-schema="infoSchema"
            :initial-values="savedInfo"
            class="p-login__form p-signup__info-form"
            @submit="submitInfo"
          >
            <div class="p-signup__section">계정 정보</div>

            <field v-slot="{ field, errors }" name="userName">
              <div>
                <label class="p-login__label">이름 <em class="p-signup__req">*</em></label>
                <input v-bind="field" type="text" name="userName" class="p-input" placeholder="홍길동" :class="{ 'is-invalid': errors.length > 0 }" />
                <error-message name="userName" class="p-login__err" />
              </div>
            </field>

            <div>
              <label class="p-login__label">이메일</label>
              <input :value="email" type="email" class="p-input" readonly disabled />
            </div>

            <template v-if="!oauthMode">
              <field v-slot="{ field, errors }" name="password">
                <div>
                  <label class="p-login__label">비밀번호 <em class="p-signup__req">*</em></label>
                  <input v-bind="field" type="password" name="password" class="p-input" placeholder="••••••••" :class="{ 'is-invalid': errors.length > 0 }" />
                  <error-message name="password" class="p-login__err" />
                </div>
              </field>

              <field v-slot="{ field, errors }" name="passwordConfirm">
                <div>
                  <label class="p-login__label">비밀번호 확인 <em class="p-signup__req">*</em></label>
                  <input v-bind="field" type="password" name="passwordConfirm" class="p-input" placeholder="••••••••" :class="{ 'is-invalid': errors.length > 0 }" />
                  <error-message name="passwordConfirm" class="p-login__err" />
                </div>
              </field>
            </template>

            <field v-slot="{ field, errors }" name="phone">
              <div>
                <label class="p-login__label">휴대폰 번호 <em class="p-signup__req">*</em></label>
                <input v-bind="field" type="tel" name="phone" class="p-input" placeholder="01012345678 (숫자만)" :class="{ 'is-invalid': errors.length > 0 }" />
                <error-message name="phone" class="p-login__err" />
              </div>
            </field>

            <div class="p-signup__section">강사 인증 정보</div>

            <field v-slot="{ field, errors }" name="orgName">
              <div>
                <label class="p-login__label">소속 기관명 <em class="p-signup__req">*</em></label>
                <input v-bind="field" type="text" name="orgName" class="p-input" placeholder="OO어학원" :class="{ 'is-invalid': errors.length > 0 }" />
                <error-message name="orgName" class="p-login__err" />
              </div>
            </field>

            <field v-slot="{ field, errors }" name="position">
              <div>
                <label class="p-login__label">직위 <em class="p-signup__req">*</em></label>
                <input v-bind="field" type="text" name="position" class="p-input" placeholder="강사, 원장 등" :class="{ 'is-invalid': errors.length > 0 }" />
                <error-message name="position" class="p-login__err" />
              </div>
            </field>

            <field v-slot="{ field }" name="subject">
              <div>
                <label class="p-login__label">담당 과목 · 교재명</label>
                <input v-bind="field" type="text" name="subject" class="p-input" placeholder="예) 영어 / OO교재" />
              </div>
            </field>

            <field v-slot="{ field }" name="reason">
              <div>
                <label class="p-login__label">요청 사유</label>
                <textarea v-bind="field" name="reason" class="p-input p-signup__textarea" rows="3" placeholder="강사 권한이 필요한 사유를 입력해주세요 (선택)" />
              </div>
            </field>

            <label class="p-signup__consent">
              <input v-model="privacyConsent" type="checkbox" />
              <span>
                <b>[필수]</b> 개인정보 수집·이용에 동의합니다.
                <span class="p-signup__consent-sub">입력하신 정보는 강사 자격 확인 목적으로만 사용됩니다.</span>
              </span>
            </label>

            <a-btn type="submit" kind="primary" class="p-login__submit" :disabled="!privacyConsent || submitting">
              {{ submitting ? '제출 중…' : '가입 및 강사 권한 요청' }}
            </a-btn>
          </vee-form>
        </template>

        <!-- ④ 완료 -->
        <template v-else-if="step === 'done'">
          <div class="p-signup__done">
            <a-pill kind="ok">접수 완료</a-pill>
            <h2 class="p-signup__done-title">가입 및 강사 권한 요청이 접수되었습니다</h2>
            <p class="p-signup__done-desc">
              관리자 확인 후 승인 결과를 메일로 안내드립니다.<br />
              승인 전에도 로그인하여 요청 상태를 확인할 수 있습니다.
            </p>
            <a-btn kind="primary" class="p-login__submit" @click="$router.push({ name: 'Signin Cover' })">로그인하기</a-btn>
          </div>
        </template>

        <div class="p-login__links">
          <span class="p-signup__foot-txt">이미 계정이 있으신가요?</span>
          <router-link class="p-login__link" :to="{ name: 'Signin Cover' }">로그인</router-link>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import axios from 'axios';
import OAuthButtons from '@/components/auth/OAuthButtons.vue';
import { Field, Form as VeeForm, ErrorMessage } from 'vee-validate';
import * as yup from 'yup';
import ABtn from '@/components/Pineapple/ABtn.vue';
import APill from '@/components/Pineapple/APill.vue';

export default {
  name: 'SignupCover',
  components: { OAuthButtons, Field, VeeForm, ErrorMessage, ABtn, APill },
  data() {
    return {
      step: 'email', // email → code → info → done
      oauthMode: false, // OAuth 가입 (비밀번호 없음, POST /instructor/access-request 로 제출)

      email: '',
      emailError: '',
      emailDuplicated: false,
      sending: false,

      code: '',
      codeError: '',
      verifying: false,
      verificationToken: null, // check-email 응답
      registrationToken: null, // verify-email 응답 (30분 유효)

      privacyConsent: false,
      submitting: false,
      // 인증 만료로 email 스텝에 되돌아가도 입력값을 보존하기 위해 보관
      savedInfo: {
        userName: '', password: '', passwordConfirm: '',
        phone: '', orgName: '', position: '', subject: '', reason: '',
      },
    };
  },
  computed: {
    infoSchema() {
      const shape = {
        userName: yup.string().trim().required('이름을 입력해주세요'),
        phone: yup
          .string()
          .required('휴대폰 번호를 입력해주세요')
          .test('digits', '숫자만 9~11자리로 입력해주세요', (v) =>
            /^\d{9,11}$/.test(String(v || '').replace(/[-\s]/g, ''))
          ),
        orgName: yup.string().trim().required('소속 기관명을 입력해주세요'),
        position: yup.string().trim().required('직위를 입력해주세요'),
        subject: yup.string(),
        reason: yup.string(),
      };
      if (!this.oauthMode) {
        shape.password = yup
          .string()
          .min(6, '비밀번호는 최소 6자 이상이어야 합니다')
          .required('비밀번호를 입력해주세요');
        shape.passwordConfirm = yup
          .string()
          .oneOf([yup.ref('password')], '비밀번호가 일치하지 않습니다')
          .required('비밀번호를 한 번 더 입력해주세요');
      }
      return yup.object(shape);
    },
    firstLanguage() {
      return navigator.language.startsWith('ko') ? 'ko' : 'en';
    },
  },
  methods: {
    stepClass(name) {
      return ['p-signup__step', { 'is-active': this.step === name }];
    },

    /* ── ① 이메일 인증 코드 발송 ── */
    async sendCode() {
      this.emailError = '';
      this.emailDuplicated = false;
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email)) {
        this.emailError = '이메일 형식이 올바르지 않습니다';
        return;
      }
      this.sending = true;
      try {
        const res = await axios.post(`${this.$CONF.API_URL}/auth/user/check-email`, {
          email: this.email,
          userType: 'instructor',
        });
        this.verificationToken = res.data.verificationToken;
        this.code = '';
        this.codeError = '';
        this.step = 'code';
      } catch (e) {
        if (e.response?.status === 400) {
          this.emailError = e.response?.data?.message || '이미 가입된 이메일입니다';
          this.emailDuplicated = true;
        } else {
          this.emailError = '인증 코드 발송에 실패했습니다. 잠시 후 다시 시도해주세요.';
        }
      } finally {
        this.sending = false;
      }
    },

    /* ── ② 인증 코드 확인 ── */
    async verifyCode() {
      this.codeError = '';
      if (!this.code) {
        this.codeError = '인증 코드를 입력해주세요';
        return;
      }
      this.verifying = true;
      try {
        const res = await axios.post(`${this.$CONF.API_URL}/auth/user/verify-email`, {
          email: this.email,
          code: this.code,
          verificationToken: this.verificationToken,
        });
        this.registrationToken = res.data.registrationToken;
        this.step = 'info';
      } catch (e) {
        if (e.response?.status === 401) {
          await this.$swal({ icon: 'warning', title: '인증 만료', text: '인증이 만료되었습니다. 이메일 인증부터 다시 진행해주세요.' });
          this.backToEmail();
        } else {
          this.codeError = e.response?.data?.message || '인증 코드가 올바르지 않습니다';
        }
      } finally {
        this.verifying = false;
      }
    },
    async resendCode() {
      if (this.sending) return;
      this.sending = true;
      this.codeError = '';
      try {
        const res = await axios.post(`${this.$CONF.API_URL}/auth/user/check-email`, {
          email: this.email,
          userType: 'instructor',
        });
        this.verificationToken = res.data.verificationToken;
        this.code = '';
        this.$notifyVue({ type: 'success', title: '인증 코드', text: '인증 코드를 다시 발송했습니다.' });
      } catch (e) {
        this.codeError = e.response?.data?.message || '코드 재발송에 실패했습니다.';
      } finally {
        this.sending = false;
      }
    },
    backToEmail() {
      this.step = 'email';
      this.code = '';
      this.codeError = '';
      this.verificationToken = null;
      this.registrationToken = null;
    },

    /* ── ③ 통합 제출 ── */
    async submitInfo(values) {
      if (!this.privacyConsent || this.submitting) return;
      this.savedInfo = { ...this.savedInfo, ...values };
      this.submitting = true;
      try {
        if (this.oauthMode) {
          // OAuth 가입: 이미 로그인됨(learner) → 권한 요청만 접수 (★ signupPath:'new')
          await this.$axios({
            method: 'post',
            url: '/instructor/access-request',
            data: {
              name: values.userName.trim(),
              phone: String(values.phone).replace(/[-\s]/g, ''),
              orgName: values.orgName.trim(),
              position: values.position.trim(),
              subject: values.subject?.trim() || '',
              reason: values.reason?.trim() || '',
              privacyConsent: true,
              signupPath: 'new',
            },
          });
          this.$router.push({ name: 'InstructorAccessStatus' });
        } else {
          // 이메일 가입: 계정 생성 + 권한 요청 동시 접수 (public)
          await axios.post(`${this.$CONF.API_URL}/instructor/access-request/signup`, {
            userName: values.userName.trim(),
            userEmail: this.email,
            password: values.password,
            phone: String(values.phone).replace(/[-\s]/g, ''),
            registrationToken: this.registrationToken,
            firstLanguage: this.firstLanguage,
            orgName: values.orgName.trim(),
            position: values.position.trim(),
            subject: values.subject?.trim() || '',
            reason: values.reason?.trim() || '',
            privacyConsent: true,
          });
          this.step = 'done';
        }
      } catch (e) {
        const status = e.response?.status;
        if (status === 401 && !this.oauthMode) {
          // registrationToken 만료(30분) — 입력값은 savedInfo 에 보존됨
          await this.$swal({ icon: 'warning', title: '인증 만료', text: '이메일 인증이 만료되었습니다. 인증을 다시 진행해주세요. 입력하신 정보는 유지됩니다.' });
          this.backToEmail();
        } else if (status === 409 && this.oauthMode) {
          await this.$swal({ icon: 'info', title: '처리 대기중', text: '이미 처리 대기중인 요청이 있습니다.' });
          this.$router.push({ name: 'InstructorAccessStatus' });
        } else {
          const msg = e.response?.data?.message || e.response?.data?.detail || '제출에 실패했습니다. 입력 내용을 확인해주세요.';
          this.$swal({ icon: 'error', title: '제출 실패', text: msg });
        }
      } finally {
        this.submitting = false;
      }
    },

    /* ── OAuth 가입 완료 → 강사 정보 입력으로 ── */
    onOAuthRegistered() {
      const user = this.$store.getters['auth/getUserInfo'];
      this.oauthMode = true;
      this.email = user?.email || '';
      this.savedInfo = { ...this.savedInfo, userName: user?.name || '' };
      this.step = 'info';
    },
    onOAuthError(e) {
      const status = e?.response?.status;
      if (status === 400 || status === 409) {
        this.$swal({
          icon: 'info',
          title: '이미 가입된 계정',
          text: "이미 가입된 계정입니다. 로그인 화면에서 소셜 로그인 후 '강사 권한 요청'을 진행해주세요.",
        });
      } else if (status) {
        this.$swal({ icon: 'error', title: '가입 실패', text: 'OAuth 가입에 실패했습니다. 잠시 후 다시 시도해주세요.' });
      }
      // status 없는 경우(사용자 팝업 취소 등)는 조용히 무시
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/_pineapple.scss';

/* ── 로그인 화면(signin/Cover.vue)과 동일한 분할 레이아웃 ── */
.p-login {
  min-height: 100vh;
  display: grid;
  grid-template-columns: 1fr 1fr;
  background: $p-bg;
  font-family: $p-font-body;
  color: $p-ink;

  @media (max-width: 900px) { grid-template-columns: 1fr; }
}

.p-mono { font-family: $p-font-mono; font-size: 11px; color: $p-ink-soft; }

.p-login__brand {
  background: $p-sidebar;
  color: $p-sidebar-tx;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px;
  /* 우측 폼이 길어져 페이지가 스크롤돼도 브랜드 패널은 화면에 고정 */
  position: sticky;
  top: 0;
  height: 100vh;
  background-image:
    radial-gradient(circle at 20% 30%, rgba(255,201,60,0.10) 0%, transparent 50%),
    radial-gradient(circle at 80% 70%, rgba(255,201,60,0.06) 0%, transparent 50%);

  @media (max-width: 900px) { display: none; }
}
.p-login__brand-inner {
  max-width: 480px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 48px;
  align-items: center;
  text-align: center;
}
.p-login__logo { display: flex; align-items: center; gap: 14px; }
.p-login__logo-img {
  width: 56px;
  height: 56px;
  object-fit: contain;
  filter: drop-shadow(0 4px 12px rgba(255, 201, 60, 0.4));
}
.p-login__brand-title {
  font-size: 22px; font-weight: 900;
  letter-spacing: -0.02em; line-height: 1.1;
  color: $p-sidebar-tx;
}
.p-login__brand-sub {
  font-size: 11px; color: $p-sidebar-mid;
  font-weight: 700; letter-spacing: 0.18em;
  text-transform: uppercase; margin-top: 4px;
}
.p-login__hero { display: flex; flex-direction: column; align-items: center; gap: 12px; }
.p-login__hero-bubble {
  background: $p-point;
  color: $p-ink;
  padding: 12px 18px;
  border-radius: 16px;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.5;
  position: relative;
  box-shadow: 0 8px 24px -8px rgba(255, 201, 60, 0.5);
  text-align: center;

  &::before {
    content: '';
    position: absolute;
    top: -8px; left: 50%;
    transform: translateX(-50%);
    border: 8px solid transparent;
    border-bottom-color: $p-point;
    border-top: 0;
  }
}

.p-login__form-side {
  display: flex;
  align-items: center;
  justify-content: center;
  /* info 스텝처럼 폼이 길어지면 내부 스크롤 대신 페이지가 자연스럽게 스크롤됨 */
  padding: 64px 48px;
}
.p-login__form-wrap {
  width: 100%;
  /* 로그인 화면과 동일 폭 — Google 버튼(GIS)이 최대 400px 까지만 렌더되므로
     이보다 넓으면 Apple 버튼·입력란과 폭이 어긋난다 */
  max-width: 400px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.p-login__header { text-align: left; }
.p-login__pill {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  background: $p-point-soft;
  color: $p-point-deep;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  margin-bottom: 12px;
}
.p-login__title {
  font-size: 26px;
  font-weight: 900;
  letter-spacing: -0.025em;
  color: $p-ink;
  margin: 0 0 8px;
  line-height: 1.2;
}
.p-login__desc { font-size: 13px; color: $p-ink-mid; margin: 0; }

.p-login__form { display: flex; flex-direction: column; gap: 16px; }
.p-login__label {
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: $p-ink;
  margin-bottom: 6px;
}
.p-input {
  width: 100%;
  height: 44px;
  padding: 0 14px;
  border: 1px solid $p-line;
  border-radius: 8px;
  background: $p-panel;
  font-family: $p-font-body;
  font-size: 13px;
  color: $p-ink;
  outline: none;

  &:focus { border-color: $p-point; box-shadow: 0 0 0 3px rgba(255, 201, 60, 0.2); }
  &.is-invalid { border-color: $p-err; }
  &::placeholder { color: $p-ink-soft; }
  &:disabled { background: $p-line-soft; color: $p-ink-mid; cursor: not-allowed; }
}
.p-login__err {
  display: block;
  margin-top: 4px;
  font-size: 11px;
  color: $p-err;
}
.p-login__submit {
  height: 44px;
  font-size: 13px;
  width: 100%;
  margin-top: 6px;
  justify-content: center;
}

.p-login__divider {
  text-align: center;
  position: relative;
  font-size: 11px;
  color: $p-ink-soft;
  font-weight: 700;
  letter-spacing: 0.1em;

  &::before {
    content: '';
    position: absolute;
    left: 0; right: 0; top: 50%;
    height: 1px; background: $p-line;
    z-index: 0;
  }
  span {
    background: $p-bg;
    padding: 0 14px;
    position: relative;
    z-index: 1;
  }
}

.p-login__links {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  padding-top: 16px;
  border-top: 1px solid $p-line;
}
.p-login__link {
  color: $p-ink-mid;
  font-weight: 700;
  text-decoration: none;

  &:hover { color: $p-ink; text-decoration: underline; }
}
.p-login__links-sep { color: $p-ink-softer; }

/* ── 회원가입 전용 ── */
.p-signup__steps {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  color: $p-ink-soft;
  font-weight: 700;
}
.p-signup__step {
  padding: 3px 8px;
  border-radius: 999px;
  background: $p-line-soft;

  &.is-active { background: $p-point; color: $p-ink; }
}
.p-signup__steps-sep { color: $p-ink-softer; }

.p-signup__notice {
  font-size: 12px;
  color: $p-ink-mid;
  line-height: 1.6;
  margin: 0;
  padding: 12px 14px;
  border: 1px solid $p-line;
  border-radius: 10px;
  background: $p-bg;

  b { color: $p-ink; font-size: 12px; }
}
.p-signup__code-input {
  font-family: $p-font-mono;
  letter-spacing: 0.2em;
  font-size: 16px;
}
.p-signup__code-actions {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  font-size: 12px;

  a {
    color: $p-ink-mid;
    font-weight: 700;
    text-decoration: none;
    &:hover { color: $p-ink; text-decoration: underline; }
  }
}
.p-signup__hint-link {
  margin-top: 6px;
  font-size: 11px;
  color: $p-ink-mid;

  a { color: $p-point-deep; font-weight: 700; }
}

.p-signup__section {
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: $p-ink-soft;
  padding-bottom: 6px;
  border-bottom: 1px solid $p-line;
  margin-top: 6px;
}
.p-signup__req { color: $p-err; font-style: normal; }
.p-signup__textarea {
  height: auto;
  padding: 10px 14px;
  resize: vertical;
  line-height: 1.5;
}
.p-signup__consent {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 12px;
  color: $p-ink-mid;
  line-height: 1.5;
  cursor: pointer;
  padding: 12px 14px;
  border: 1px solid $p-line;
  border-radius: 10px;
  background: $p-bg;

  input {
    margin-top: 2px;
    accent-color: $p-point-deep;
    cursor: pointer;
  }
  b { color: $p-ink; }
}
.p-signup__consent-sub {
  display: block;
  font-size: 11px;
  color: $p-ink-soft;
  margin-top: 2px;
}

.p-signup__done {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
}
.p-signup__done-title {
  font-size: 18px;
  font-weight: 900;
  letter-spacing: -0.02em;
  margin: 0;
}
.p-signup__done-desc {
  font-size: 13px;
  color: $p-ink-mid;
  line-height: 1.6;
  margin: 0;
}
.p-signup__foot-txt { color: $p-ink-soft; }
</style>
